import { ChangeDetectionStrategy, AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import {PresenceService} from './common/services/presence.service';
import {AuthGuardService} from './common/services/auth-guard.service';
import {NotificationsService} from './common/services/notifications.service';
import {Localization} from '../../projects/common/src/lib/interface/localization.extension';
import {StringsService} from '../../projects/common/src/lib/services/strings.service';
import {ActivatedRoute, Router} from '@angular/router';
import {UpdateService} from './common/services/update.service';
import {Logger} from './common/utils/logger';
import {Subscription} from 'rxjs';
import {SettingsService} from './canvas/services/settings.service';
import {CookieConsentService} from './common/consent/cookie-consent.service';
import {OverlayContainer} from "@angular/cdk/overlay";
import {ThemeId} from "./common/enums/theme-id";
import {ThemeService} from "./common/services/theme.service";
import {setUnitStrings} from './canvas/models/measure-units';



@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss', 'styles/common.scss'],
  styles: []
})
export class AppComponent extends Localization implements AfterViewInit, OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private overlay = inject(OverlayContainer);
  settingsService = inject(SettingsService);
  private cookieConsent = inject(CookieConsentService);
  private themeService = inject(ThemeService);
  private updateService = inject(UpdateService);
  private authGuard = inject(AuthGuardService);
  /** Started here so the feed, its toasts and the link prompt run on every page, not only where a toolbar is. */
  private notifications = inject(NotificationsService);

  /** Computed from authGuard signal — Angular automatically re-renders when this changes */
  readonly showLoader = computed(() => this.authGuard.loader());
  readonly loadingProgress = signal(20);
  readonly underMaintenance = signal(false);
  /** A new version is ready: the update screen covers the app until it is reloaded. */
  readonly updateRequired = computed(() => this.updateService.required());
  readonly reloading = signal(false);

  themes = ThemeId;

  constructor() {

    super();
    const updateService = this.updateService;

    Logger.log('LANGUAGE: ', navigator.language);

    // Unit names and the units written as words ("acres") are read by static
    // code with no injector; they follow whatever language is current.
    setUnitStrings(key => {
      const strings = this.localization.getStrings();
      return (strings && strings[key]) || '';
    });


    // While the update screen is up, the app under it is out of reach: no
    // focus, no clicks, and no key reaches its shortcuts (a Delete on the map).
    effect(() => {
      const blocked = updateService.required();
      const host: HTMLElement = this.elementRef.nativeElement;
      const outside = [...Array.from(host.children), document.querySelector('.cdk-overlay-container')]
        .filter((el): el is Element => !!el && !el.classList.contains('update-overlay'));
      outside.forEach(el => blocked ? el.setAttribute('inert', '') : el.removeAttribute('inert'));
      for (const type of ['keydown', 'keyup', 'keypress']) {
        if (blocked) {
          window.addEventListener(type, this.swallowKey, true);
        } else {
          window.removeEventListener(type, this.swallowKey, true);
        }
      }
      if (blocked) {
        setTimeout(() => document.getElementById('update-reload')?.focus());
      }
    });
  }

  /** Keys while the update screen is up: none reach the app; Enter or Space reloads. Ctrl+R / F5 still work. */
  private swallowKey = (event: KeyboardEvent) => {
    event.stopImmediatePropagation();
    if (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.reloadForUpdate();
    }
  };

  reloadForUpdate() {
    if (this.reloading()) {
      return;
    }
    this.reloading.set(true);
    this.updateService.reload();
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: FocusEvent): void {
    Logger.log('onFocus tab');
    this.updateService.checkForUpdates();
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: FocusEvent): void {
    Logger.log('onBlur tab');
  }

  ngOnInit(): void {
    // Cookie consent: reads the stored choice and starts analytics only if
    // the visitor agreed. Inside an embed nothing optional runs and nothing is
    // asked -- the visitor is on the host's website, not ours.
    this.cookieConsent.init();
  }

  ngAfterViewInit() {
    this.elementRef.nativeElement.ownerDocument.body.style.backgroundColor = '#ffffff';
  }

  ngOnDestroy(): void {}

  private setTheme() {
    const darkClassName = 'dark-theme';
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    document.body.classList.toggle(darkClassName, prefersDark.matches);
  }

  private toggleTheme() {
    document.body.classList.toggle('dark-theme');
  }
}
